import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Link,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { apiService } from '../services/api';
import SEO from '../components/SEO';
import { DEMO_INTEREST_OPTIONS, DemoInterestId } from '../config/demoInterestOptions';

const RequestDemoPage: React.FC = () => {
  const [contactName, setContactName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [emailOptIn, setEmailOptIn] = useState(true);
  const [smsOptIn, setSmsOptIn] = useState(true);
  const [interests, setInterests] = useState<DemoInterestId[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  // Encourage both: when user fills a channel, keep that opt-in on; don't force the empty one
  useEffect(() => {
    if (!email.trim()) setEmailOptIn(false);
  }, [email]);
  useEffect(() => {
    if (!phone.trim()) setSmsOptIn(false);
  }, [phone]);

  const hasEmail = email.trim().length > 0;
  const hasPhone = phone.trim().length > 0;

  const canSubmit = useMemo(() => {
    if (loading) return false;
    if (contactName.trim().length < 2) return false;
    if (businessName.trim().length < 2) return false;
    if (zipCode.replace(/\D/g, '').length < 5) return false;
    if (!hasEmail && !hasPhone) return false;
    if (interests.length < 1) return false;
    const willEmail = emailOptIn && hasEmail;
    const willSms = smsOptIn && hasPhone;
    return willEmail || willSms;
  }, [
    loading,
    contactName,
    businessName,
    zipCode,
    hasEmail,
    hasPhone,
    interests,
    emailOptIn,
    smsOptIn,
  ]);

  const toggleInterest = (id: DemoInterestId) => {
    setInterests((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!canSubmit) {
      setError('Fill the required fields, pick at least one interest, and choose email and/or text delivery.');
      return;
    }
    setLoading(true);
    try {
      const res = await apiService.post('/leads/demo-request', {
        contactName: contactName.trim(),
        businessName: businessName.trim(),
        zipCode: zipCode.trim(),
        email: hasEmail ? email.trim() : undefined,
        phone: hasPhone ? phone.trim() : undefined,
        interests,
        emailOptIn: emailOptIn && hasEmail,
        smsOptIn: smsOptIn && hasPhone,
      });
      if (!res.success) {
        throw new Error((res as any).error || 'Could not submit request');
      }
      setDone(true);
    } catch (err: any) {
      setError(err?.message || 'Could not submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Request an Interactive LawnHub Demo"
        description="Request temporary login credentials to explore LawnHub’s provider platform with sample customers, schedules, crews, and routes."
        url="/request-demo"
      />
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Paper elevation={2} sx={{ p: { xs: 3, md: 4 } }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
            Request an interactive demo
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Tell us a bit about your business and what you want to try. We’ll send login credentials
            automatically — full guide by email, short tips by text. One active visitor at a time,
            about 30 minutes per session.
          </Typography>

          {done ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              You’re all set — check your email and/or texts for the login and guide. Jump in when you’re
              ready, and log out when finished so the next person can get a turn.
            </Alert>
          ) : (
            <Box component="form" onSubmit={handleSubmit}>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <TextField
                label="Your name"
                required
                fullWidth
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                sx={{ mb: 2 }}
                autoComplete="name"
              />
              <TextField
                label="Lawn business name"
                required
                fullWidth
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                sx={{ mb: 2 }}
                autoComplete="organization"
              />
              <TextField
                label="ZIP code"
                required
                fullWidth
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value)}
                sx={{ mb: 2 }}
                inputProps={{ inputMode: 'numeric', maxLength: 10 }}
              />

              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                How should we send your login?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Email <strong>or</strong> phone is required — both is best.
              </Typography>
              <TextField
                label="Email"
                type="email"
                fullWidth
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (e.target.value.trim()) setEmailOptIn(true);
                }}
                sx={{ mb: 1 }}
                autoComplete="email"
              />
              <FormControlLabel
                sx={{ mb: 2, alignItems: 'flex-start' }}
                control={
                  <Checkbox
                    checked={emailOptIn && hasEmail}
                    disabled={!hasEmail}
                    onChange={(e) => setEmailOptIn(e.target.checked)}
                    color="primary"
                  />
                }
                label="Email me the demo login and full guide"
              />
              <TextField
                label="Mobile phone"
                fullWidth
                value={phone}
                onChange={(e) => {
                  setPhone(e.target.value);
                  if (e.target.value.trim()) setSmsOptIn(true);
                }}
                sx={{ mb: 1 }}
                helperText="US 10-digit number"
              />
              <FormControlLabel
                sx={{ mb: 2, alignItems: 'flex-start' }}
                control={
                  <Checkbox
                    checked={smsOptIn && hasPhone}
                    disabled={!hasPhone}
                    onChange={(e) => setSmsOptIn(e.target.checked)}
                    color="primary"
                  />
                }
                label="Text me the login and a short tip list (msg & data rates may apply; reply STOP to opt out)"
              />

              <FormLabel component="legend" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                What are you looking for? (pick at least one)
              </FormLabel>
              <FormHelperText sx={{ mt: 0, mb: 1 }}>
                We’ll tailor the short text tips to what you check.
              </FormHelperText>
              <FormGroup sx={{ mb: 3 }}>
                {DEMO_INTEREST_OPTIONS.map((opt) => (
                  <FormControlLabel
                    key={opt.id}
                    control={
                      <Checkbox
                        checked={interests.includes(opt.id)}
                        onChange={() => toggleInterest(opt.id)}
                        color="primary"
                      />
                    }
                    label={opt.label}
                  />
                ))}
              </FormGroup>

              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={!canSubmit}
                sx={{ fontWeight: 700, py: 1.25 }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Send my demo login'}
              </Button>
              <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1.5, textAlign: 'center' }}>
                We use your information to send your demo login and follow up about LawnHub. See our{' '}
                <Link component={RouterLink} to="/privacy-policy" target="_blank" rel="noopener">
                  Privacy Policy
                </Link>
                .
              </Typography>
            </Box>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
            Prefer to jump in as a provider?{' '}
            <Button component={RouterLink} to="/get-started" size="small" sx={{ textTransform: 'none' }}>
              Create a free account
            </Button>
          </Typography>
        </Paper>
      </Container>
    </>
  );
};

export default RequestDemoPage;
